
import {
  defineComponent,
  onBeforeMount,
  onBeforeUnmount,
  reactive,
  ref,
} from "vue";
import Page from "../atoms/Page.vue";
import {
  GetMyProfileResponseMembership,
  GetMyUsage,
  GetMyPlan,
  MyApi,
  Profile,
} from "../../autogen";
import { useRouter, useRoute } from "vue-router";
import Instruction from "../atoms/Instruction.vue";
import { Instruction as InstrctionType } from "../../types";
import Moment from "moment";
import { download } from "../../helpers/dom-utils";
import UIkit from "uikit";
import InquiryForm from "../molecules/InquiryForm.vue";
import EvaluationForm from "../molecules/EvaluationForm.vue";
type AlertModalInfo = {
  actionName: string;
  messages: string[];
  onClick: Function;
};
type ConfirmModalInfo = {
  actionName1: string;
  actionName2: string;
  title: string;
  messages: string[];
  enableButton: boolean;
  onClick1: Function;
  onClick2: Function;
};
type State = {
  disabled: boolean;
  profile: Profile | null;
  membership: GetMyProfileResponseMembership | null;
  translation_file: File | null;
  translating: boolean;
  translated_file: { name?: string; size?: number } | null;
  error: string | null;
  usage: GetMyUsage | null;
  evaluationFormKey: number;
  inquiryFormKey: number;
  downloading: boolean;
  confirmModalInfo: ConfirmModalInfo;
  count: number;
  instruction?: InstrctionType;
  alertModalInfo: AlertModalInfo;
  showPopup: boolean;
  appliedPlan?: GetMyPlan;
  plans: GetMyPlan[];
  source_lang: string;
  target_lang: string;
  force_ocr: boolean;
};
export default defineComponent({
  components: { Page, Instruction, InquiryForm, EvaluationForm },
  setup() {
    const myApi = new MyApi();
    const route = useRoute();
    const router = useRouter();
    const fileInput = ref<HTMLInputElement>();
    const evaluationModal = ref<HTMLElement>();
    const state: State = reactive({ 
      disabled: true,
      profile: null,
      membership: null,
      translation_file: null,
      translating: false,
      translated_file: null,
      error: null,
      usage: null,
      evaluationFormKey: 0,
      inquiryFormKey: 0,
      downloading: false,
      source_lang: 'EN',
      target_lang: 'JA',
      force_ocr: false,
      count: 1,
      confirmModalInfo: {
        actionName1: '',
        actionName2: '',
        enableButton: true,
        title: '',
        messages: [],
        onClick1: () => {},
        onClick2: () => {},
      },
      alertModalInfo: {
        actionName: '',
        messages: [],
        onClick: () => {},
      },
      showPopup: false,
      plans: [],
    });

    async function updateState() {
      const response = await myApi.getMyProfile({});
      state.profile = response.data.profile;
      state.membership = response.data.membership;
      state.membership.contract_renewal_date = Moment(
        response.data.membership.contract_renewal_date
      ).format("YYYY年MM月DD日");
    }
    onBeforeMount(async () => {
      await updateState();
      const response = await myApi.getMyPlans({});
      updatePlans(response.data);

      const isPaymentSucceeded = route.query.success;
      if (isPaymentSucceeded) {
        state.instruction = {
          level: "info",
          message: `回数を追加購入しました`,
        };
      }
      router.replace({ query: undefined });
    });
    const onUploadFileChange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files![0].size > 104857600) {
        fileInput.value!.value = "";
        setTimeout(() => {
          state.error = "翻訳ファイルのサイズの上限は100MBです";
        });
        return;
      }
      state.translation_file = target.files![0];
    };
    const onFileDrop = (event: DragEvent) => {
      if (state.membership!.quota === 0) return;
      const files = event.dataTransfer!.files;
      state.translation_file = files[0];
    };
    const onUploadFileCloseButtonClick = () => {
      fileInput.value!.value = "";
      state.translation_file = null;
      state.translating = false;
      state.translated_file = null;
      state.error = null;
    };
    let interval: number;
    const additionalPurchaseButtonClick = async (_plan: GetMyPlan) => {
      state.alertModalInfo = {
        actionName: "購入手続き",
        messages: ["追加購入する回数を入力してください"],
        onClick: () => additionalPurchaseConfirmed(_plan),
      };
      setTimeout(() => {
        if (alertModalRef.value) {
          UIkit.modal(alertModalRef.value).show();
        }
      });
    };
    // state.count を更新するための関数を追加
    const updatePurchaseCount = (event: Event) => {
      const target = event.target as HTMLInputElement;
      state.count = parseInt(target.value, 10);
    };
    const onUploadButtonClick = async () => {
      state.translating = true;
      state.error = null;
      state.usage = null;
      try {
        const confirmResponse = await myApi.confirmRetranslation({
          forceOcr: state.force_ocr,
          file: state.translation_file!,
        });
        state.translating = false;
        const confirm_state = confirmResponse.data['state'].split('/')
        if (confirm_state[0] == 'no_usage') {
          uploadFile();
        }
        else if (confirm_state[0] == 'processing') {
          state.confirmModalInfo = {
            actionName1: '戻る',
            actionName2: '',
            enableButton: false,
            title: '翻訳中です',
            messages: [
            '翻訳完了後、利用履歴からダウンロードできます。',
            '完了まで少々お待ち下さい。'
            ],
            onClick1:() => onUploadFileCloseButtonClick(),
            onClick2:() => {},
          }
          setTimeout(() => {
            if (confirmModalRef.value) {
              UIkit.modal(confirmModalRef.value).show();
            }
          });          
        }
        else if (confirm_state[0] == 'confirm') {
          if (confirm_state[1] == 'API') {
            state.confirmModalInfo = {
              actionName1: '再翻訳しない',
              actionName2: '再翻訳する',
              enableButton: true,
              title: '最新バージョンではない翻訳データがあります',
              messages: [
              '再翻訳を行うと精度が改善される可能性があります。最新バージョンで翻訳を行いますか？（チケットが消費されます）'
              ],
              onClick1:() => onUploadFileCloseButtonClick(),
              onClick2:() => uploadFile(),
            }
            setTimeout(() => {
              if (confirmModalRef.value) {
                UIkit.modal(confirmModalRef.value).show();
              }
            });
          }
        }
        else if(confirm_state[0] == 'full_translated') {
          const usage_id = confirmResponse.data['id']
          state.confirmModalInfo = {
            actionName1: 'ダウンロードしない',
            actionName2: 'ダウンロードする',
            enableButton: true,
            title: '同条件での翻訳済みのデータがあります',
            messages: [
            '利用履歴からダウンロードを行いますか？'
            ],
            onClick1:() => onUploadFileCloseButtonClick(),
            onClick2:(id?: number) => downloadFile(usage_id, false),
          }
          setTimeout(() => {
            if (confirmModalRef.value) {
              UIkit.modal(confirmModalRef.value).show();
            }
          });
        }
        // 翻訳履歴があったが、完全翻訳結果がない場合は再翻訳の提示
        else if(confirm_state[0] == 'translated') {
          // OCR強制時は、再翻訳提示なし
          if (state.force_ocr) {
            uploadFile()
          }
          else{
            state.confirmModalInfo = {
              actionName1: '再翻訳しない',
              actionName2: '再翻訳する',
              enableButton: true,
              title: '完了していない翻訳データが存在します',
              messages: [
              '以前、翻訳チケットの残数が足りなかった等の理由により、完了していない翻訳データを発見しました。',
              '再翻訳を行いますか？（チケットが消費されます）'
              ],
              onClick1:() => onUploadFileCloseButtonClick(),
              onClick2:() => uploadFile(),
            }
            setTimeout(() => {
              if (confirmModalRef.value) {
                UIkit.modal(confirmModalRef.value).show();
              }
            });
          }
        }
        else {
          state.error = "データ取得に失敗しました";
        }
      } catch (err) {
        console.error(err);
        clearInterval(interval);
        state.translating = false;
        state.error = "翻訳に失敗しました";
      }
    };
    const uploadFile  = async () => {
      try{
        state.translating = true;
        const uploadResponse = await myApi.uploadMyFile({
          sourceLang: state.source_lang,
          targetLang: state.target_lang,
          forceOcr: state.force_ocr,
          file: state.translation_file!,
        });
        // const uploadResponse = { status: 200, data: { id: 0 } };
        if (uploadResponse.status === 200) {
          interval = window.setInterval(async () => {
            const usageResponse = await myApi.getMyUsage({
              usageId: uploadResponse.data.id!,
            });
            // 翻訳中じゃない
            if (usageResponse.data.status && usageResponse.data.status !== 202) {
              state.translating = false;
              state.usage = usageResponse.data;

              clearInterval(interval);
              // エラー終了
              if (usageResponse.data.status !== 200 && usageResponse.data.status !== 201) {
                if (usageResponse.data.err_state == "1") {
                  state.error = '翻訳に失敗しました<br/>文字データが埋め込まれていない（文字データをコピー＆ペーストできない）PDF です。<br/>翻訳には文字認識（OCR）機能が必要です。<br/>スタンダートもしくはエンタープライズプランをご利用ください。';
                }
                else if (usageResponse.data.err_state == "2") {
                  state.error = '翻訳に失敗しました<br/>本サービスはpdfファイル（.pdf）以外の翻訳には対応していません。アップロードしたファイルの再確認をお願いします。';
                }
                else {
                  state.error = "翻訳に失敗しました";
                }
              }
              // 翻訳完了
              else {
                state.translated_file = {};
              }
            }
          }, 1000);
        }
      } catch (err) {
        console.error(err);
        clearInterval(interval);
        state.translating = false;
        state.error = "翻訳に失敗しました";
      }
    };
    const downloadFile = async (usage_id?: number, evaluate?: boolean) => {
      state.error = null;
      state.downloading = true;
      try {
        const response = await myApi.downloadMyFile(
          {
            usageId: usage_id!,
          },
          {
            responseType: "arraybuffer",
          }
        );
        await updateState();
        download(`${state.translation_file!.name}_翻訳済.docx`, response.data);
        if(evaluate){
          document.body.onfocus = () => {
            if (evaluationModal.value) {
              state.evaluationFormKey = Date.now();
              UIkit.modal(evaluationModal.value).show();
            }
            document.body.onfocus = null;
          };
        }
      } catch (err) {
        state.error = "ダウンロードに失敗しました";
      }
      state.downloading = false;
    }
    const onDownloadButtonClick = async () => {
      downloadFile(state.usage!.id, false);
    };
    onBeforeUnmount(() => {
      if (interval) {
        clearInterval(interval);
      }
    });
    const onAlertClose = () => {
      state.error = null;
    };
    const onInquiryButtonClick = () => {
      state.inquiryFormKey = Date.now();
      UIkit.modal(inquiryModalRef.value!).show();
    };
    const inquiryModalRef = ref<HTMLElement>();
    const onInquirySubmitted = () => {
      if (inquiryModalRef.value) {
        UIkit.modal(inquiryModalRef.value).hide();
      }
    };
    const onSubmit = () => {
      if (evaluationModal.value) {
        UIkit.modal(evaluationModal.value).hide();
      }
    };
    const fileSize = (size?: number) => {
      return size !== undefined
        ? `${Math.round((size / 1024 / 1024) * 10) / 10}MB`
        : "-";
    };
    const fileName = (name: string) => name;
    // 追加購入ボタン用
    const showPopup = ref(false);

    async function updatePlans(plans: GetMyPlan[]) {
      state.plans = plans;
      state.plans.sort((p1, p2) => {
        if (p1.applied) return 1;
        if (p2.applied) return -1;
        return (p1.price || 0) < (p2.price || 0)
          ? 1
          : (p1.price || 0) > (p2.price || 0)
          ? -1
          : 0;
      });
      state.appliedPlan = plans.find((p) => p.applied);
    }
    const additionalPurchaseConfirmed = async (plan: GetMyPlan) => {
      // 決済リンク作成
      const stripe_session = await myApi.createStripePaymentSession({ stripePaymentSessionRequest: { quantity: state.count } });
      // 決済リンクへリダイレクト
      location.href = stripe_session.data.url;
      if (alertModalRef.value) {
        UIkit.modal(alertModalRef.value).hide();
      }
    };
    const goToPlan = () => {
      router.push("/plan");
    };
    const alertModalRef = ref<HTMLElement>();
    const confirmModalRef = ref<HTMLElement>();
    
    return {
      fileInput,
      evaluationModal,
      state,
      onUploadFileChange,
      onUploadFileCloseButtonClick,
      onUploadButtonClick,
      onAlertClose,
      onFileDrop,
      onDownloadButtonClick,
      onInquiryButtonClick,
      onInquirySubmitted,
      inquiryModalRef,
      alertModalRef,
      onSubmit,
      fileSize,
      fileName,
      showPopup,
      additionalPurchaseConfirmed,
      additionalPurchaseButtonClick,
      confirmModalRef,
      goToPlan,
      updatePurchaseCount
    };
  },
});
